import api from '@/utils/api';

export interface OrderReportRequest {
  from_date: string;
  to_date: string;
  period?: string;
  tags?: string[];
  financial_status?: string;
}

// Summary Report
export async function getOrdersSummary(req: OrderReportRequest) {
  return api.req({
    url: '/reports/orders/summary',
    method: api.Method.POST,
    data: req,
  });
}

// Date-wise Report
export async function getOrdersDateWise(req: OrderReportRequest) {
  return api.req({
    url: '/reports/orders/date-wise',
    method: api.Method.POST,
    data: req,
  });
}

// Tag-wise Report
export async function getOrdersTagWise(req: OrderReportRequest) {
  return api.req({
    url: '/reports/orders/tag-wise',
    method: api.Method.POST,
    data: req,
  });
}

// Agent-wise Report
export async function getOrdersAgentWise(req: OrderReportRequest) {
  return api.req({
    url: '/reports/orders/agent-wise',
    method: api.Method.POST,
    data: req,
  });
}

// Status Matrix Report
export async function getOrdersStatusMatrix(req: OrderReportRequest) {
  return api.req({
    url: '/reports/orders/status-matrix',
    method: api.Method.POST,
    data: req,
  });
}

// ---------------------------------------------------------------------------
// Agent Performance Reports
// ---------------------------------------------------------------------------

export interface AgentReportRequest {
  from_date: string;
  to_date: string;
  lead_bucket?: string;   // "abandoned_cart" | "reorder" | "regular" | undefined = all
  agent_uuid?: string;
}

export async function getAgentLeaderboard(req: AgentReportRequest) {
  return api.req({ url: '/reports/agents/leaderboard', method: api.Method.POST, data: req });
}

export async function getAgentDispositionBreakdown(req: AgentReportRequest) {
  return api.req({ url: '/reports/agents/disposition-breakdown', method: api.Method.POST, data: req });
}

export async function getAgentProducts(req: AgentReportRequest) {
  return api.req({ url: '/reports/agents/products', method: api.Method.POST, data: req });
}

export async function getAgentResponseTime(req: AgentReportRequest) {
  return api.req({ url: '/reports/agents/response-time', method: api.Method.POST, data: req });
}

export async function getAgentProductMatrix(req: AgentReportRequest) {
  return api.req({ url: '/reports/agents/product-matrix', method: api.Method.POST, data: req });
}

// Agent Dashboard -- revenue by agent from frozen sales credit
// (backend: /reports/agents/dashboard*, app/services/sales_credit_service.py)
export type AgentStage = 'delivered' | 'in_transit' | 'awaiting_pickup' | 'returned' | 'cancelled';

export interface AgentDetailRequest {
  from_date: string;
  to_date: string;
  lead_bucket?: string | null;
  /** an agent's uuid, 'agents' = every credited order, 'none' = orders no agent earned */
  agent_uuid?: string | null;
  payment_status?: 'cod' | 'prepaid' | 'partial' | null;
  delivery_status?: AgentStage | null;
}

export async function getAgentDashboard(req: { from_date: string; to_date: string; lead_bucket?: string | null }) {
  return api.req({ url: '/reports/agents/dashboard', method: api.Method.POST, data: req });
}

export async function getAgentOrders(req: AgentDetailRequest) {
  return api.req({ url: '/reports/agents/dashboard/orders', method: api.Method.POST, data: req });
}

export async function getAgentLeads(req: AgentDetailRequest) {
  return api.req({ url: '/reports/agents/dashboard/leads', method: api.Method.POST, data: req });
}

// Incentive Leaderboard
//
// The response carries three blocks, not one: `data` (agents), `unattributed`
// (revenue that could not be pinned on an agent, one row per reason) and
// `totals` (the two summed server-side). The page must read all three -- the
// agent rows alone are about 21% of revenue.
export interface IncentiveLeaderboardResponse {
  data: unknown[];
  unattributed?: unknown[];
  totals?: Record<string, number>;
  data_as_of?: string;
}

export async function getIncentiveLeaderboard(req: { from_date: string; to_date: string; lead_bucket?: string | null }) {
  return api.req<IncentiveLeaderboardResponse>({ url: '/reports/agents/incentive-leaderboard', method: api.Method.POST, data: req });
}

// Incentive Leaderboard drill-downs
export interface IncentiveDetailRequest {
  from_date: string;
  to_date: string;
  lead_bucket?: string | null;
  agent_uuid?: string | null;
  payment_status?: 'cod' | 'partial' | 'prepaid' | 'voided' | null;
  delivery_status?: 'pending' | 'in_transit' | 'delivered' | 'rto' | null;
}

export async function getIncentiveOrders(req: IncentiveDetailRequest) {
  return api.req({ url: '/reports/agents/incentive-leaderboard/orders', method: api.Method.POST, data: req });
}

export async function getIncentiveLeads(req: IncentiveDetailRequest) {
  return api.req({ url: '/reports/agents/incentive-leaderboard/leads', method: api.Method.POST, data: req });
}

// ABC (Abandoned Cart) Report
export async function getAbcReport(req: { from_date: string; to_date: string }) {
  return api.req({ url: '/reports/abc', method: api.Method.POST, data: req });
}
